import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { mapWithConcurrencySettled } from '@numeo/types-core'
import { fieldAutofillState, type AutofillOffer } from '../utils/documentAutofill/merge'
import { planCard, type AutofillMemory, type CardSummary } from '../utils/documentAutofill/cardPlan'
import { readDocumentFile, type FileRead, type ReadDocument, type ReadJob } from '../utils/documentAutofill/readFile'
import { revealFirstField } from '../utils/documentAutofill/revealField'
import { canTryAgain, MAX_AUTOFILL_RETRIES, type AutofillProblem, type AutofillTarget, type AutofillValue } from '../utils/documentAutofill/targets'
import { trackBoxTruckProfileDocumentAutofillAction, trackBoxTruckProfileDocumentAutofillApplied, type BoxTruckDocumentScope } from '../utils/analytics'

/** Files of one card read at once, so a card of five files does not take five of the server's immediate-mode slots. */
const MAX_PARALLEL_READS = 2
const NO_LOCKS: ReadonlySet<never> = new Set()

export type { AutofillProblem }

export interface AutofillFileProblem {
    /** The file's id on its card (`AutofillSource.id`), so the card can act on it. */
    sourceId: string
    fileName: string
    problem: AutofillProblem
    /** For a wrong document: the type the file was read as, and whether that came from an earlier read of the same file. */
    readAs?: string
    cached?: boolean
    /** Try again left for this file: only a failed or timed-out read is tried again. */
    retriesLeft: number
}

/**
 * What a card shows about its read. `read` lists the files read (`readIds`), what their values did (`summary`, null when no file was
 * read as the card's type) and each file that filled nothing.
 */
export type AutofillCardStatus =
    | { status: 'reading'; files: number }
    | { status: 'read'; files: number; summary: CardSummary | null; problems: AutofillFileProblem[]; readIds: string[] }

/**
 * Where a card's read brings HR, most wanted first: the fields that need a decision (a value offered beside HR's, files that
 * disagree); else, when it filled something, the top of the block that holds most of the card's fields (`anchor`), then what it filled.
 */
const fieldsToCheck = <K extends string>({ offered, filled }: AutofillMemory<K>, card: string, anchor: K): Array<ReadonlySet<string>> => {
    const decide = new Set<string>()
    for (const key in offered) if (offered[key]?.card === card) decide.add(key)
    if (decide.size > 0) return [decide]
    const checked = new Set<string>()
    for (const key in filled) if (filled[key]?.card === card) checked.add(key)
    return checked.size > 0 ? [new Set([anchor]), checked] : []
}

/** A field's mark: green while it holds a document's value; orange with the documents' values it does not hold (`disagree`: the files differ). */
export type AutofillFieldMark = { state: 'filled' } | { state: 'differs'; offers: AutofillOffer[]; disagree: boolean }

/** A file on a card. */
export interface AutofillSource {
    /** One id for the file's whole life, across renders, remounts and its save (see `sourceIds`). */
    id: string
    fileName: string
    /** How it is read: a pending file uploaded with the request, a stored document read by the server from storage. */
    read: ReadDocument
}

/** A card's running read: one controller per file. Its results reach the card only when every file is done, so Cancel leaves no trace. */
interface Run<K extends string> {
    controllers: Map<string, AbortController>
    results: Map<string, FileRead<K>>
}

interface Options<K extends string> {
    /** Whose form it is, for analytics. */
    scope: BoxTruckDocumentScope
    /** The cards this form reads, by their profile doc type. */
    targets: Partial<Record<string, AutofillTarget<K>>>
    /** The form's current value for a field. */
    valueOf: (key: K) => unknown
    /** Write values into the form in one change; `null` empties the field. */
    apply: (values: Partial<Record<K, AutofillValue | null>>) => void
    /** Fields the form does not let a document change (a terminated driver's identity). */
    locked?: ReadonlySet<K>
    /** Fields the form does not show now (an owner marked "no company"). A document never replaces or empties what HR can't see: an empty one is filled, a value only offered. */
    hidden?: ReadonlySet<K>
    /** Show these hidden fields: a read offers a value for them, and HR has to see it to press Use. */
    reveal?: (keys: K[]) => void
    /**
     * Values a document put in the form before it opened (the drivers-list CDL scan), under the `card` they came from: they show green
     * like a read's, and a card read later may replace them while HR has not changed them. Taken on the first render only.
     */
    initialFilled?: { card: string; values: Partial<Record<K, AutofillValue>> }
}

const initialMemory = <K extends string>(initialFilled: Options<K>['initialFilled']): AutofillMemory<K> => {
    const filled: AutofillMemory<K>['filled'] = {}
    if (initialFilled) {
        for (const key in initialFilled.values) {
            const value = initialFilled.values[key]
            if (value !== undefined) filled[key] = { value, card: initialFilled.card }
        }
    }
    return { filled, offered: {} }
}

/**
 * AI autofill for one profile form. HR reads a card: every file on it not read yet is read, two at a time, and when all are done the
 * card's values go into the form by `planCard` (values the files agree on fill the empty fields and are offered beside HR's; a field
 * the files disagree on offers every value). Removing a file plans the card again without it. Cancel stops the running read and keeps
 * what the card filled before. A failed or timed-out file can be tried again `MAX_AUTOFILL_RETRIES` times. How a field shows is
 * derived from what the form holds on every render, so HR typing over a value clears its mark. Every read is aborted on unmount.
 */
export function useDocumentAutofill<K extends string>({ scope, targets, valueOf, apply, locked, hidden, reveal, initialFilled }: Options<K>) {
    const [cards, setCards] = useState<Partial<Record<string, AutofillCardStatus>>>({})
    const [memory, setMemory] = useState<AutofillMemory<K>>(() => initialMemory(initialFilled))
    // Refs are the source of truth, ahead of the render: two cards that finish in the same tick each plan on the other's result.
    const statuses = useRef(cards)
    const remembered = useRef(memory)
    const reads = useRef(new Map<string, Map<string, FileRead<K>>>())
    const runs = useRef(new Map<string, Run<K>>())
    // The latest form, for a read that ends after a re-render. A layout effect, so it is current before any promise can resolve.
    const form = useRef({ valueOf, apply, locked, hidden, reveal })
    // What was written since the form last rendered: the form's values show it only once React commits, and a second card can settle
    // before that. Read first, so that card does not see the first card's fields as empty (as fields HR emptied).
    const unrendered = useRef<Partial<Record<K, AutofillValue | null>>>({})
    useLayoutEffect(() => {
        form.current = { valueOf, apply, locked, hidden, reveal }
        unrendered.current = {}
    })

    // The scroll to a field a read wants checked, still waiting for the form to render it.
    const revealing = useRef<() => void>(() => undefined)

    useEffect(() => {
        const active = runs.current
        const reveal = revealing
        return () => {
            active.forEach((run) => run.controllers.forEach((controller) => controller.abort()))
            active.clear()
            reveal.current()
        }
    }, [])

    const setCard = useCallback((card: string, status: AutofillCardStatus | undefined) => {
        statuses.current = { ...statuses.current, [card]: status }
        setCards(statuses.current)
    }, [])

    const remember = useCallback((next: AutofillMemory<K>) => {
        remembered.current = next
        setMemory(next)
    }, [])

    /** Put the card's reads into the form and its status line: after a read, a removed file, or Cancel. */
    const settle = useCallback(
        (card: string): { summary: CardSummary; files: number; problems: number } => {
            const fileReads = [...(reads.current.get(card) ?? new Map<string, FileRead<K>>())]
            const files = fileReads.flatMap(([, read]) => (read.outcome.kind === 'facts' ? [{ fileName: read.fileName, values: read.outcome.values }] : []))
            const { valueOf: rendered, apply: write, locked: lockedNow, hidden: hiddenNow, reveal: show } = form.current
            const written = unrendered.current
            const current = (key: K): unknown => (key in written ? (written[key] ?? undefined) : rendered(key))
            const plan = planCard({ card, files, current, memory: remembered.current, locked: lockedNow ?? NO_LOCKS, hidden: hiddenNow ?? NO_LOCKS })
            if (Object.keys(plan.write).length > 0) {
                write(plan.write)
                unrendered.current = { ...unrendered.current, ...plan.write }
            }
            // A new offer for a field the form hides is shown: HR sees the document's value beside theirs before anything can change.
            const offeredHidden = (Object.keys(plan.memory.offered) as K[]).filter(
                (key) => hiddenNow?.has(key) && plan.memory.offered[key]?.card === card && !remembered.current.offered[key]
            )
            remember(plan.memory)
            if (offeredHidden.length > 0) show?.(offeredHidden)
            const problems = fileReads.flatMap(([id, read]): AutofillFileProblem[] => {
                const { outcome } = read
                if (outcome.kind === 'facts') return []
                return [
                    {
                        sourceId: id,
                        fileName: read.fileName,
                        problem: outcome.kind,
                        readAs: outcome.readAs,
                        cached: outcome.cached,
                        retriesLeft: MAX_AUTOFILL_RETRIES - read.retries,
                    },
                ]
            })
            const readIds = fileReads.map(([id]) => id)
            setCard(
                card,
                readIds.length === 0 ? undefined : { status: 'read', files: readIds.length, summary: files.length > 0 ? plan.summary : null, problems, readIds }
            )
            return { summary: plan.summary, files: readIds.length, problems: problems.length }
        },
        [remember, setCard]
    )

    /**
     * `settle`, for every caller: when the form refuses the write (its own update threw), the card must still leave "Reading…", or it
     * stays there with Cancel and the AI button both dead until the form is closed. Its files are offered to fill by hand.
     */
    const settleCard = useCallback(
        (card: string): ReturnType<typeof settle> | undefined => {
            try {
                return settle(card)
            } catch (error) {
                Sentry.captureException(error, { tags: { feature: 'profile-document-autofill', operation: 'apply' } })
                const kept = [...(reads.current.get(card) ?? new Map<string, FileRead<K>>())]
                const readIds = kept.map(([id]) => id)
                const problems = kept.map(([id, read]): AutofillFileProblem => ({ sourceId: id, fileName: read.fileName, problem: 'failed', retriesLeft: 0 }))
                setCard(card, readIds.length === 0 ? undefined : { status: 'read', files: readIds.length, summary: null, problems, readIds })
                return undefined
            }
        },
        [settle, setCard]
    )

    const run = useCallback(
        (card: string, jobs: Array<ReadJob<K>>) => {
            const target = targets[card]
            if (!target || jobs.length === 0 || runs.current.has(card)) return
            const active: Run<K> = { controllers: new Map(jobs.map((job) => [job.id, new AbortController()])), results: new Map() }
            runs.current.set(card, active)
            setCard(card, { status: 'reading', files: jobs.length })
            void mapWithConcurrencySettled(jobs, MAX_PARALLEL_READS, async (job) => {
                const signal = active.controllers.get(job.id)?.signal
                if (!signal || signal.aborted) return
                const read = await readDocumentFile(scope, card, target, job, signal)
                if (read && !signal.aborted) active.results.set(job.id, read)
            })
                .then(() => {
                    // Cancelled, or the form closed: the card keeps what it had.
                    if (runs.current.get(card) !== active) return
                    runs.current.delete(card)
                    const kept = reads.current.get(card) ?? new Map<string, FileRead<K>>()
                    // A file removed while it was read is left out (its controller is gone).
                    active.results.forEach((read, id) => {
                        if (active.controllers.has(id)) kept.set(id, read)
                    })
                    reads.current.set(card, kept)
                    const settled = settleCard(card)
                    if (!settled) return
                    trackBoxTruckProfileDocumentAutofillApplied(scope, card, { files: settled.files, problems: settled.problems, ...settled.summary })
                    revealing.current()
                    revealing.current = revealFirstField(fieldsToCheck(remembered.current, card, target.anchor))
                })
                .catch((error: unknown) => {
                    // Nothing above is expected to throw; if it does, it is reported, and a card it left on "Reading…" is settled, so
                    // Cancel and the AI button are not dead until the form closes. A newer run of the card is left alone.
                    Sentry.captureException(error, { tags: { feature: 'profile-document-autofill', operation: 'run' } })
                    if (runs.current.get(card) === active) runs.current.delete(card)
                    if (!runs.current.has(card) && statuses.current[card]?.status === 'reading') settleCard(card)
                })
        },
        [targets, scope, settleCard, setCard]
    )

    /** Read the card's files that were not read yet. */
    const start = useCallback(
        (card: string, sources: readonly AutofillSource[]) => {
            const known = reads.current.get(card)
            const fresh = sources.filter((source) => !known?.has(source.id))
            if (fresh.length === 0 || runs.current.has(card)) return
            trackBoxTruckProfileDocumentAutofillAction(scope, card, 'start', { files: fresh.length })
            run(card, fresh)
        },
        [run, scope]
    )

    /** Read again the card's files that failed or timed out, while they have retries left (Try again). */
    const retry = useCallback(
        (card: string) => {
            const jobs = [...(reads.current.get(card) ?? new Map<string, FileRead<K>>())]
                .filter(([, read]) => canTryAgain(read.outcome.kind, read.retries))
                .map(([id, read]) => ({ id, fileName: read.fileName, read: read.read, prior: read }))
            if (jobs.length === 0) return
            trackBoxTruckProfileDocumentAutofillAction(scope, card, 'retry', { files: jobs.length })
            run(card, jobs)
        },
        [run, scope]
    )

    /** Stop the card's running read (Cancel). What the card filled before stays; the files are offered for reading again. */
    const cancel = useCallback(
        (card: string) => {
            const active = runs.current.get(card)
            if (!active) return
            runs.current.delete(card)
            active.controllers.forEach((controller) => controller.abort())
            trackBoxTruckProfileDocumentAutofillAction(scope, card, 'cancel')
            settleCard(card)
        },
        [scope, settleCard]
    )

    /** A file left the card: its read stops, and the card is planned again without it (the fields only it filled are emptied). */
    const forget = useCallback(
        (card: string, id: string) => {
            const active = runs.current.get(card)
            active?.controllers.get(id)?.abort()
            active?.controllers.delete(id)
            const removed = reads.current.get(card)?.delete(id)
            // A running read plans the card when it ends.
            if (removed && !active) settleCard(card)
        },
        [settleCard]
    )

    const fieldState = useCallback(
        (key: K): AutofillFieldMark | undefined => {
            const entry = memory.offered[key]
            const state = fieldAutofillState(valueOf(key), memory.filled[key]?.value, entry?.offers)
            // Files that disagree show every value, the one the field may already hold too: HR sees what each file says.
            if (state?.state === 'differs' && entry && entry.offers.length > 1) return { state: 'differs', offers: entry.offers, disagree: true }
            if (state?.state === 'differs') return { ...state, disagree: false }
            return state
        },
        [valueOf, memory]
    )

    /** Write a document's value over HR's: the field turns green, and the value is HR's now (removing the file keeps it). */
    const accept = useCallback(
        (key: K, value: AutofillValue) => {
            const { filled, offered } = remembered.current
            const entry = offered[key]
            if (!entry?.offers.some((offer) => offer.value === value)) return
            const values: Partial<Record<K, AutofillValue>> = {}
            values[key] = value
            try {
                form.current.apply(values)
                unrendered.current = { ...unrendered.current, ...values }
            } catch (error) {
                // The form refused the value: the offer stays, so HR can type it or press Use again.
                Sentry.captureException(error, { tags: { feature: 'profile-document-autofill', operation: 'use' } })
                return
            }
            const nextFilled = { ...filled }
            nextFilled[key] = { value, card: null }
            const nextOffered = { ...offered }
            delete nextOffered[key]
            remember({ filled: nextFilled, offered: nextOffered })
            trackBoxTruckProfileDocumentAutofillAction(scope, entry.card, 'use', { field: key })
        },
        [remember, scope]
    )

    return { cards, start, retry, cancel, forget, fieldState, accept }
}
